import { CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import {
  calculatePastePositions,
  GridClipboardData,
  copyToClipboard,
  createClipboardData,
  pasteFromClipboard,
  saveClipboardToStorage,
  loadClipboardFromStorage,
} from '@/shared/ui/data/EditableGrid/lib/clipboard';
import React, { useCallback, useEffect, useState } from 'react';
import { Bound, Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { Column, validateCellValue } from '@/shared/ui/data/EditableGrid/types/column.types';

interface IOptions {
  getCellValue: (position: Position) => CellValue;
  setCellValue: (position: Position, value: CellValue) => void;
  bound: Bound;
  columns?: ReadonlyArray<Column>;
  onCopy?: (data: GridClipboardData) => void;
  onPaste?: (positions: ReadonlyArray<{ position: Position; value: CellValue }>) => void;
}

export function useClipboard(options: IOptions) {
  const { getCellValue, setCellValue, bound, columns, onCopy, onPaste } = options;

  const [clipboardData, setClipboardData] = useState<GridClipboardData | null>(() =>
    loadClipboardFromStorage()
  );
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isPasting, setIsPasting] = useState<boolean>(false);

  useEffect(() => {
    const stored = loadClipboardFromStorage();
    if (stored) setClipboardData(stored);
  }, []);

  const handleCopy = useCallback(
    async (selectedPositions: ReadonlyArray<Position>) => {
      if (selectedPositions.length === 0) return;

      setIsCopying(true);

      try {
        const data = createClipboardData(selectedPositions, getCellValue);
        await copyToClipboard(data);
        setClipboardData(data);
        saveClipboardToStorage(data);
        onCopy?.(data);
      } catch {
        const data = createClipboardData(selectedPositions, getCellValue);
        setClipboardData(data);
        saveClipboardToStorage(data);
        onCopy?.(data);
      } finally {
        setIsCopying(false);
      }
    },
    [getCellValue, onCopy]
  );

  const handlePaste = useCallback(
    async (startPosition: Position) => {
      setIsPasting(true);

      let dataSource: GridClipboardData | null = null;

      try {
        dataSource = await pasteFromClipboard();
      } catch {
        // Clipboard API failed, try fallbacks
      }

      if (!dataSource) {
        dataSource = clipboardData || loadClipboardFromStorage();
      }

      if (dataSource) {
        const getColumn = (colIndex: number) => columns?.[colIndex];
        const pastePositions = calculatePastePositions(startPosition, dataSource, bound, getColumn);

        pastePositions.forEach(({ position, value }) => {
          const column = columns?.[position.colIndex];
          const validatedValue = column ? validateCellValue(value, column) : value;
          setCellValue(position, validatedValue);
        });

        onPaste?.(pastePositions);
        if (!clipboardData) setClipboardData(dataSource);
      } else {
        alert('붙여넣을 데이터가 없습니다. 먼저 셀을 복사해주세요.');
      }

      setIsPasting(false);
    },
    [setCellValue, bound, onPaste, clipboardData, columns]
  );

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      selectedPositions: ReadonlyArray<Position>,
      focusedCell: Position | null
    ) => {
      const isMac = navigator.userAgent.includes('Mac');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      const isCopyKey = e.key.toLowerCase() === 'c' && modifier && !e.shiftKey;
      if (isCopyKey) {
        e.preventDefault();
        const cellsToCopy =
          selectedPositions.length > 0 ? selectedPositions : focusedCell ? [focusedCell] : [];
        if (cellsToCopy.length > 0) handleCopy(cellsToCopy);
        return;
      }

      const isPasteKey = e.key.toLowerCase() === 'v' && modifier && !e.shiftKey;
      if (isPasteKey && focusedCell) {
        e.preventDefault();
        handlePaste(focusedCell);
        return;
      }
    },
    [handleCopy, handlePaste]
  );

  return {
    clipboardData,
    isCopying,
    isPasting,
    handleCopy,
    handlePaste,
    handleKeyDown,
  };
}
